import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ProveedorService } from '../../services/proveedor.service';
import { ReportsService } from '../../services/reports.service';
import { ToastrService } from 'ngx-toastr';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import moment from 'moment';
import 'moment/locale/es';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

interface SupplierSummaryResponse {
  proveedorId: number;
  proveedorNombre: string;
  periodo: { start: string; end: string };
  resumen: {
    totalVentas: number;
    totalProductosVendidos: number;
    gananciasTotales: number;
    promedioPorVenta: number;
    cantidadVentas: number;
  };
  productos: Array<{
    productoId: number;
    productoNombre: string;
    productoCodigo: string;
    cantidadVendida: number;
    totalVendido: number;
    precioPromedio: number;
    porcentajeDelTotal: number;
    ultimaVenta?: string;
  }>;
  ventasPorFecha: Array<{
    fecha: string;
    cantidadVentas: number;
    totalVendido: number;
    productosVendidos: number;
  }>;
  tendenciaMensual: Array<{
    mes: string;
    totalVendido: number;
    cantidadVentas: number;
  }>;
}

@Component({
  selector: 'app-supplier-report',
  templateUrl: './supplier-report.component.html',
  styleUrls: ['./supplier-report.component.scss']
})
export class SupplierReportComponent implements OnInit {
  filterForm: FormGroup;
  loadingProviders = false;
  loadingReport = false;
  providers: any[] = [];
  reportData: SupplierSummaryResponse | null = null;
  productosOrdenados: SupplierSummaryResponse['productos'] = [];
  top5Productos: SupplierSummaryResponse['productos'] = [];

  periodTitle = '';
  periodSubtitle = '';
  private customDateChangeTimeout: any = null;

  @ViewChild('dayPicker') dayPicker!: MatDatepicker<Date>;

  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Fecha'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Ventas ($)'
        },
        beginAtZero: true
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: context => {
            const value = Number(context.raw) || 0;
            return new Intl.NumberFormat('es-MX', {
              style: 'currency',
              currency: 'MXN'
            }).format(value);
          }
        }
      }
    }
  };
  barChartType: ChartType = 'bar';
  barChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: { display: true, text: 'Fecha' }
      },
      y: {
        position: 'left',
        title: { display: true, text: 'Total vendido ($)' },
        beginAtZero: true
      },
      y1: {
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Cantidad de ventas' }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: context => {
            const value = Number(context.raw) || 0;
            return new Intl.NumberFormat('es-MX', {
              style: 'currency',
              currency: 'MXN'
            }).format(value);
          }
        }
      }
    }
  };
  lineChartType: ChartType = 'line';
  lineChartData: ChartData<'line'> = { labels: [], datasets: [] };

  constructor(
    private fb: FormBuilder,
    private proveedorService: ProveedorService,
    private reportsService: ReportsService,
    private toastr: ToastrService
  ) {
    moment.locale('es');
    this.filterForm = this.fb.group({
      proveedorId: [null],
      viewType: ['today'],
      startDate: [null],
      endDate: [null]
    });
  }

  ngOnInit(): void {
    this.loadProviders();
    this.setDefaultDateRange();
    this.updatePeriodDisplay();
    this.filterForm.get('proveedorId')?.valueChanges.subscribe(value => {
      this.onFiltersChanged();
    });
    this.filterForm.get('viewType')?.valueChanges.subscribe((viewType) => {
      this.setDefaultDateRange();
      // Si se cambia a personalizado, no hacer fetch hasta que se seleccionen las fechas
      if (viewType !== 'custom') {
        this.onFiltersChanged();
      } else {
        // Si cambia a personalizado, solo actualizar el display
        this.updatePeriodDisplay();
      }
    });
    this.filterForm.get('startDate')?.valueChanges.subscribe(() => {
      if (this.filterForm.get('viewType')?.value === 'custom') {
        this.handleCustomDateChange();
      }
    });
    this.filterForm.get('endDate')?.valueChanges.subscribe(() => {
      if (this.filterForm.get('viewType')?.value === 'custom') {
        this.handleCustomDateChange();
      }
    });
  }

  private handleCustomDateChange(): void {
    // Limpiar timeout anterior si existe
    if (this.customDateChangeTimeout) {
      clearTimeout(this.customDateChangeTimeout);
    }

    // Esperar un poco para que el usuario termine de seleccionar ambas fechas
    this.customDateChangeTimeout = setTimeout(() => {
      const start = this.filterForm.get('startDate')?.value;
      const end = this.filterForm.get('endDate')?.value;
      
      // Solo actualizar si ambas fechas están seleccionadas
      if (start && end) {
        this.onFiltersChanged(true);
      } else {
        // Si falta alguna fecha, solo actualizar el display
        this.updatePeriodDisplay();
      }
    }, 500);
  }

  private loadProviders(): void {
    this.loadingProviders = true;
    this.proveedorService.findAll().subscribe({
      next: (providers) => {
        this.providers = providers || [];
        this.loadingProviders = false;
      },
      error: () => {
        this.loadingProviders = false;
        this.toastr.error('No se pudieron cargar los proveedores');
      }
    });
  }

  onFiltersChanged(fetchOnCustom: boolean = true): void {
    const proveedorId = this.filterForm.get('proveedorId')?.value;
    if (!proveedorId) {
      this.reportData = null;
      this.productosOrdenados = [];
      this.top5Productos = [];
      this.resetCharts();
      this.updatePeriodDisplay();
      return;
    }

    const viewType = this.filterForm.get('viewType')?.value;
    const start = this.filterForm.get('startDate')?.value;
    const end = this.filterForm.get('endDate')?.value;

    // Si es modo personalizado, verificar que ambas fechas estén seleccionadas
    if (viewType === 'custom') {
      if (!start || !end) {
        this.updatePeriodDisplay();
        return;
      }
    }

    // Si no es modo personalizado o si las fechas están completas, actualizar y buscar
    this.updatePeriodDisplay();
    this.fetchReport(proveedorId);
  }

  private setDefaultDateRange(): void {
    const viewType = this.filterForm.get('viewType')?.value;
    let start: moment.Moment | null = null;
    let end: moment.Moment | null = null;

    switch (viewType) {
      case 'week':
        start = moment().startOf('isoWeek');
        end = moment().endOf('isoWeek');
        break;
      case 'month':
        start = moment().startOf('month');
        end = moment().endOf('month');
        break;
      case 'custom':
        // En modo personalizado, no establecer fechas por defecto
        // Mantener las fechas que el usuario haya seleccionado
        const currentStart = this.filterForm.get('startDate')?.value;
        const currentEnd = this.filterForm.get('endDate')?.value;
        if (currentStart && currentEnd) {
          start = moment(currentStart);
          end = moment(currentEnd);
        } else {
          // Si no hay fechas seleccionadas, no establecer ninguna
          return;
        }
        break;
      case 'today':
      default:
        start = moment().startOf('day');
        end = moment().endOf('day');
        break;
    }

    if (start && end) {
      this.filterForm.patchValue({
        startDate: start.toDate(),
        endDate: end.toDate()
      }, { emitEvent: false });
    }
    this.updatePeriodDisplay();
  }

  private fetchReport(proveedorId: number): void {
    this.loadingReport = true;
    this.reportData = null;
    this.resetCharts();

    const { startDate, endDate } = this.filterForm.value;
    if (!startDate || !endDate) {
      this.toastr.warning('Selecciona un rango de fechas válido');
      this.loadingReport = false;
      return;
    }

    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    this.reportsService.post({ start, end }, `proveedor/${proveedorId}/detalle`).subscribe({
      next: (data: SupplierSummaryResponse) => {
        this.reportData = this.normalizeReportData(data);
        this.productosOrdenados = this.getProductosOrdenados(this.reportData?.productos);
        this.top5Productos = this.getTop5Productos(this.reportData?.productos);
        this.updateCharts();
        this.updatePeriodDisplay();
        this.loadingReport = false;
      },
      error: () => {
        this.toastr.error('Error al obtener el reporte del proveedor');
        this.loadingReport = false;
      }
    });
  }

  private normalizeReportData(data: SupplierSummaryResponse | null): SupplierSummaryResponse | null {
    if (!data) {
      return null;
    }

    const resumen = data.resumen
      ? {
          totalVentas: Number(data.resumen.totalVentas ?? 0),
          totalProductosVendidos: Number(data.resumen.totalProductosVendidos ?? 0),
          gananciasTotales: Number(data.resumen.gananciasTotales ?? 0),
          promedioPorVenta: Number(data.resumen.promedioPorVenta ?? 0),
          cantidadVentas: Number(data.resumen.cantidadVentas ?? 0)
        }
      : undefined;

    const productos = (data.productos ?? []).map(producto => {
      const rawVentas = (producto as any).ventas as Array<{ fecha: string }> | undefined;
      const ultimaVentaDesdeVentas = rawVentas && rawVentas.length
        ? rawVentas
            .map(v => v.fecha)
            .filter(fecha => !!fecha)
            .sort((a, b) => moment(b).valueOf() - moment(a).valueOf())[0]
        : null;

      return {
        ...producto,
        cantidadVendida: Number(producto.cantidadVendida ?? 0),
        totalVendido: Number(producto.totalVendido ?? 0),
        precioPromedio: Number(producto.precioPromedio ?? 0),
        porcentajeDelTotal: Number(producto.porcentajeDelTotal ?? 0),
        ultimaVenta:
          producto.ultimaVenta ??
          (producto as any).fechaUltimaVenta ??
          (producto as any).fechaVenta ??
          ultimaVentaDesdeVentas ??
          null
      };
    });

    const ventasPorFecha = (data.ventasPorFecha ?? []).map(item => ({
      ...item,
      cantidadVentas: Number(item.cantidadVentas ?? 0),
      totalVendido: Number(item.totalVendido ?? 0),
      productosVendidos: Number((item as any).productosVendidos ?? 0)
    }));

    const tendenciaMensual = (data.tendenciaMensual ?? []).map(item => ({
      ...item,
      totalVendido: Number(item.totalVendido ?? 0),
      cantidadVentas: Number(item.cantidadVentas ?? 0)
    }));

    return {
      ...data,
      resumen,
      productos,
      ventasPorFecha,
      tendenciaMensual
    };
  }

  private getProductosOrdenados(productos: SupplierSummaryResponse['productos'] | undefined): SupplierSummaryResponse['productos'] {
    if (!productos) {
      return [];
    }
    return [...productos].sort((a, b) => {
      const fechaA = a.ultimaVenta ? moment(a.ultimaVenta).valueOf() : 0;
      const fechaB = b.ultimaVenta ? moment(b.ultimaVenta).valueOf() : 0;
      return fechaB - fechaA;
    });
  }

  private getTop5Productos(productos: SupplierSummaryResponse['productos'] | undefined): SupplierSummaryResponse['productos'] {
    if (!productos) {
      return [];
    }
    return [...productos]
      .sort((a, b) => b.cantidadVendida - a.cantidadVendida)
      .slice(0, 5);
  }

  private updateCharts(): void {
    if (!this.reportData) {
      this.resetCharts();
      return;
    }

    const ventasPorFechaOrdenadas = [...(this.reportData.ventasPorFecha ?? [])].sort((a, b) =>
      moment(a.fecha).diff(moment(b.fecha))
    );

    // Ventas por fecha (bar chart)
    const fechas = ventasPorFechaOrdenadas.map(item => moment(item.fecha).format('DD/MM/YYYY'));
    const ventas = ventasPorFechaOrdenadas.map(item => Number(item.totalVendido ?? 0));

    this.barChartData = {
      labels: fechas,
      datasets: [
        {
          label: 'Ventas diarias',
          data: ventas,
          backgroundColor: 'rgba(59, 130, 246, 0.55)',
          borderColor: 'rgba(37, 99, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    // Tendencia por fecha (line chart) - usando ventasPorFecha
    const fechasLinea = ventasPorFechaOrdenadas.map(item => moment(item.fecha).format('DD/MM/YYYY'));
    const totalVendidoPorFecha = ventasPorFechaOrdenadas.map(item => Number(item.totalVendido ?? 0));
    const cantidadVentasPorFecha = ventasPorFechaOrdenadas.map(item => Number(item.cantidadVentas ?? 0));

    this.lineChartData = {
      labels: fechasLinea,
      datasets: [
        {
          label: 'Total vendido',
          data: totalVendidoPorFecha,
          fill: false,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.4)',
          tension: 0.3,
          pointRadius: 5,
          pointBackgroundColor: 'rgba(34, 197, 94, 1)',
          pointHoverRadius: 7,
          pointHoverBackgroundColor: 'rgba(34, 197, 94, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        },
        {
          label: 'Cantidad de ventas',
          data: cantidadVentasPorFecha,
          fill: false,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.4)',
          tension: 0.3,
          pointRadius: 5,
          pointBackgroundColor: 'rgba(59, 130, 246, 1)',
          pointHoverRadius: 7,
          pointHoverBackgroundColor: 'rgba(59, 130, 246, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          yAxisID: 'y1'
        }
      ]
    };
  }

  private resetCharts(): void {
    this.barChartData = {
      labels: [],
      datasets: [
        {
          label: 'Ventas diarias',
          data: [],
          backgroundColor: 'rgba(59, 130, 246, 0.55)',
          borderColor: 'rgba(37, 99, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    this.lineChartData = {
      labels: [],
      datasets: [
        {
          label: 'Total vendido',
          data: [],
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.4)',
          fill: false,
          tension: 0.3,
          pointRadius: 5
        },
        {
          label: 'Cantidad de ventas',
          data: [],
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.4)',
          fill: false,
          tension: 0.3,
          pointRadius: 5,
          yAxisID: 'y1'
        }
      ]
    };
  }

  changePeriod(direction: 'prev' | 'next'): void {
    const viewType = this.filterForm.get('viewType')?.value ?? 'today';
    let start = this.filterForm.get('startDate')?.value ? moment(this.filterForm.get('startDate')?.value) : moment();
    let end = this.filterForm.get('endDate')?.value ? moment(this.filterForm.get('endDate')?.value) : moment();

    const step = direction === 'next' ? 1 : -1;

    switch (viewType) {
      case 'today':
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
      case 'week':
        start = start.add(step, 'week').startOf('isoWeek');
        end = start.clone().endOf('isoWeek');
        break;
      case 'month':
        start = start.add(step, 'month').startOf('month');
        end = start.clone().endOf('month');
        break;
      case 'custom':
        const currentStart = this.filterForm.get('startDate')?.value ? moment(this.filterForm.get('startDate')?.value).startOf('day') : moment().startOf('day');
        const currentEnd = this.filterForm.get('endDate')?.value ? moment(this.filterForm.get('endDate')?.value).endOf('day') : moment().endOf('day');
        const diffDays = currentEnd.diff(currentStart, 'days');
        start = currentStart.add(step * (diffDays + 1), 'days');
        end = start.clone().add(diffDays, 'days').endOf('day');
        break;
      default:
        start = start.add(step, 'day').startOf('day');
        end = start.clone().endOf('day');
        break;
    }

    this.filterForm.patchValue(
      {
        startDate: start.toDate(),
        endDate: end.toDate()
      },
      { emitEvent: false }
    );

    this.updatePeriodDisplay();
    const proveedorId = this.filterForm.get('proveedorId')?.value;
    if (proveedorId) {
      this.fetchReport(proveedorId);
    }
  }

  private updatePeriodDisplay(): void {
    const viewType = this.filterForm.get('viewType')?.value ?? 'today';
    const startValue = this.filterForm.get('startDate')?.value;
    const endValue = this.filterForm.get('endDate')?.value;

    if (!startValue || !endValue) {
      this.periodTitle = this.getViewTitle(viewType);
      this.periodSubtitle = 'Selecciona un rango de fechas para ver el reporte.';
      return;
    }

    const start = moment(startValue);
    const end = moment(endValue);

    this.periodTitle = this.getViewTitle(viewType);
    this.periodSubtitle = this.buildPeriodSubtitle(viewType, start, end);
  }

  private getViewTitle(viewType: string): string {
    switch (viewType) {
      case 'today':
        return 'Vista del día';
      case 'week':
        return 'Semana seleccionada';
      case 'month':
        return 'Mes seleccionado';
      case 'custom':
        return 'Rango personalizado';
      default:
        return 'Periodo seleccionado';
    }
  }

  private buildPeriodSubtitle(viewType: string, start: moment.Moment, end: moment.Moment): string {
    // Asegurar que el locale esté en español
    const startEs = start.locale('es');
    const endEs = end.locale('es');
    
    const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
    const formatDay = (date: moment.Moment) => {
      const formatted = date.format('dddd D [de] MMMM YYYY');
      return capitalize(formatted);
    };

    if (viewType === 'today' || startEs.isSame(endEs, 'day')) {
      return formatDay(startEs);
    }

    if (viewType === 'week') {
      return `Del ${formatDay(startEs)} al ${formatDay(endEs)}`;
    }

    if (viewType === 'month') {
      return capitalize(startEs.format('MMMM YYYY'));
    }

    return `Del ${formatDay(startEs)} al ${formatDay(endEs)}`;
  }

  get hasReport(): boolean {
    return !!this.reportData;
  }

  get resumen() {
    return this.reportData?.resumen;
  }

  get periodo() {
    return this.reportData?.periodo;
  }

  openDayPicker(): void {
    const viewType = this.filterForm.get('viewType')?.value;
    if (viewType === 'today' && this.dayPicker) {
      this.dayPicker.open();
    }
  }

  onDaySelected(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (date) {
      const selectedDate = moment(date);
      this.filterForm.patchValue({
        startDate: selectedDate.startOf('day').toDate(),
        endDate: selectedDate.endOf('day').toDate()
      }, { emitEvent: false });
      this.updatePeriodDisplay();
      const proveedorId = this.filterForm.get('proveedorId')?.value;
      if (proveedorId) {
        this.fetchReport(proveedorId);
      }
    }
  }

  get isDayView(): boolean {
    return this.filterForm.get('viewType')?.value === 'today';
  }
}

